import React, { useContext, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";

import "../../constants/service_constants";

import DashboardTable from "../../components/DashboardTable";
import { TableSkeleton } from "../../components/Skeleton";
import EmptyState from "../../components/EmptyState";
import {
  Card,
  Container,
  Row,
  Col,
  Button,
} from "react-bootstrap";
import axios from "../../utils/axiosConfig";
import SitewideDataContext from "../../utils/sitewideContext";
import CollectUserInfoModal from "../../components/Modals/CollectUserInfoModal";
import * as Sentry from "@sentry/react";

function mapSitesResponse(sites) {
  return (sites || []).map((site) => {
    if (site.status) {
      return {
        site_name: site.site_name,
        site_id: site.site_id,
        icon: "",
        site_url: site.site_url,
        pages: site.status.pagecount,
        last_crawl: site.last_crawl,
        last_healthy_crawl: site.last_crawl,
        enabled: true,
        active: site.active,
        crawl_frequency: site.crawl_frequency,
        created: site.created,
        current_crawl: site.current_crawl,
        ignore_case: site.ignore_case,
        local_only: site.local_only,
        owner: site.owner,
        pageLimit: site.pageLimit,
        poll_frequency: site.poll_frequency,
        save_html: site.save_html,
        save_pdfs: site.save_pdfs,
        spider: site.spider,
        status: site.status,
        take_screenshot: site.take_screenshot,
        updated: site.updated,
        users: site.users,
      };
    }
    return {
      site_id: site.site_id,
      icon: "",
      site_url: site.site_url,
      site_name: site.site_name,
      pages: 'n/a',
      last_crawl: 'Site has not been crawled yet',
      last_healthy_crawl: 'n/a',
      enabled: false,
    };
  });
}

function Dashboard() {
  const { getAccessTokenSilently } = useAuth0();
  const { sitewideData, setSitewideData } = useContext(SitewideDataContext);
  const [showCollectModal, setShowCollectModal] = useState(false);
  const [userData, setUserData] = useState({});
  const [sites, setSites] = useState([]);
  const [tableMessage, setTableMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently);
  getAccessTokenSilentlyRef.current = getAccessTokenSilently;
  const setSitewideDataRef = useRef(setSitewideData);
  setSitewideDataRef.current = setSitewideData;

  useEffect(() => {
    async function getAccessToken() {
      const accessToken = await getAccessTokenSilentlyRef.current();
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    }

    async function initializeDashboard() {
      setLoading(true);
      setError(null);
      try {
        await getAccessToken();

        const userResponse = await axios.get(API_ADDRESS + USER_ENDPOINT);
        setUserData(userResponse.data);
        if (!userResponse.data.first_name || !userResponse.data.last_name || !userResponse.data.phone_number) {
          setShowCollectModal(true);
        }
        setSitewideDataRef.current((prev) => (!prev.userInfo ? { ...prev, userInfo: userResponse.data } : prev));

        const response = await axios.get(API_ADDRESS + SITES_ENDPOINT);
        const mappedSites = mapSitesResponse(response.data.sites);
        setSites(mappedSites);
        if (mappedSites.length === 0) {
          setTableMessage('You have no sites. Click the button below to add a new site.');
        } else {
          setTableMessage('');
        }
    } catch (e) {
      console.error(e);
      Sentry.captureException(e);
      setError(e);
      setTableMessage('');
    } finally {
        setSitewideDataRef.current((prev) => ({ ...prev, overloading: false }));
        setLoading(false);
      }
    }

    initializeDashboard();
  }, [retryCount]);

  return (
    <>
      {showCollectModal &&
          <CollectUserInfoModal
            isOpen={showCollectModal}
            setIsOpen={setShowCollectModal}
            userData={userData}
          />
      }
      <Container fluid className="wmp-fade-in">
        <Row>
          <Col md="12">
            <Card className="dashboard-sites-card">
              <Card.Header className="card-header--flex">
                <Card.Title as="h4" className="card-header__title">
                  Sites
                  <small className="card-header__count">({sites.length})</small>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {loading && (
                  <TableSkeleton rows={6} cols={3} />
                )}
                {error && !loading && (
                  <div className="error-state text-center py-5 px-4">
                    <p className="text-danger mb-3 mb-md-2">Failed to load sites. Please try again.</p>
                    <Button variant="primary" onClick={() => setRetryCount((c) => c + 1)}>
                      Retry
                    </Button>
                  </div>
                )}
                {!loading && !error && sites.length === 0 && (
                  <EmptyState
                    heading="No sites yet"
                    copy="Add your first site to start monitoring changes."
                    buttonLabel="Add your first site"
                    buttonTo="/addsite"
                  />
                )}
                {!loading && !error && sites.length > 0 && (
                  <>
                    <DashboardTable
                        key={sites.length}
                        records={sites}
                        userInfo={userData}
                        message={tableMessage}
                        columns={[
                          {
                            Header: "Site Name",
                            accessor: "site",
                            id: "site",
                            filter: "text",
                            sortType: (rowA, rowB, columnId) => {
                              const a = rowA.original.site_value;
                              const b = rowB.original.site_value;
                              return a === b ? 0 : a > b ? 1 : -1;
                            },
                          },
                          {
                            Header: "Pages",
                            accessor: "pages",
                            id: "1",
                          },
                          {
                            Header: "Last Crawl",
                            accessor: "last_crawl",
                            id: "3",
                            sortType: (rowA, rowB, columnId) => {
                              const a = rowA.original.last_crawl_value;
                              const b = rowB.original.last_crawl_value;
                              return a === b ? 0 : a > b ? 1 : -1;
                            },
                          },
                        ]}
                        className="-striped -highlight none"
                    />
                    <div className="dashboard-add-site-cta flex-column justify-content-center flex flex-wrap mt-4 mb-2 align-content-center">
                      <Link to="/addsite">
                        <span className="dashboard-add-site-cta__icon" aria-hidden="true">
                          <i className="fa-3x fa-solid fa-circle-plus text-success" />
                        </span>
                        <span className="dashboard-add-site-cta__label text-success">Add a new site</span>
                      </Link>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default Dashboard;
